import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  CopyToClipboard,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { dissociateMetadataFormFromResource, listResourcesAssociatedToMetadataEntity } from 'src/api/catalog';
import { defaultWrapLinesPreference, notificationsPageSizePreference, paginationLabels } from 'src/commons/tables';
import { convertResourceARNToLink, getScopeForArn } from 'src/components/utils/arnUtil';
import { EmptyState } from 'src/commons/EmptyState';
import { getResourceNameFromArn } from 'src/components/dataadvisory/constants';

export interface MetadataFormAssociationsTableProps {
  form: any;
  ownerId: string;
  setNotification: (header: any, message: string) => void;
}

const MetadataFormAssociationsTable = (props: MetadataFormAssociationsTableProps) => {
  const [metadataFormAssociations, setMetadataFormAssociations] = useState([]);
  const [selectedAssociatedResourceArn, setSelectedAssociatedResourceArn] = useState(undefined);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loadingFormAssociations, setLoadingFormAssociations] = useState(false);
  const [formAssociationsTableMessage, setFormAssociationsTableMessage] = useState('No associations available');
  const [dissociateFormModalVisible, setDissociateFormModalVisible] = useState(false);
  const formId = props.form.MetadataFormId;
  const formOwnerId = props.form.OwnerId;
  const [nextToken, setNextToken] = useState(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    metadataFormAssociations == undefined ? [] : metadataFormAssociations,
    {
      filtering: {
        noMatch: '',
        empty: <EmptyState title={formAssociationsTableMessage} subtitle='No associated resources were found.' />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [],
      },
    },
  );

  const metadataFormAssociationColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'resourceName',
      header: 'Resource name',
      cell: (item) => (
        <Link href={convertResourceARNToLink(item.Resource)}>{getResourceNameFromArn(item.Resource)}</Link>
      ),
      minWidth: 150,
      sortingField: 'ResourceName',
    },
    {
      id: 'resourceArn',
      header: 'Resource ARN',
      cell: (item) => (
        <CopyToClipboard
          textToCopy={item.Resource}
          variant={'inline'}
          copySuccessText={'Successfully copied ARN'}
          copyErrorText={'Failed to copy ARN'}
        />
      ),
      minWidth: 300,
      sortingField: 'Resource',
    },
    {
      id: 'resourceType',
      header: 'Resource type',
      cell: (item) => getScopeForArn(item.Resource),
      minWidth: 200,
    },
  ];

  useEffect(() => {
    const { selectedItems } = collectionProps;
    const selectedFormAssociation = selectedItems[0];
    if (selectedFormAssociation) {
      setSelectedAssociatedResourceArn(selectedFormAssociation.Resource);
    }
  }, [collectionProps.selectedItems]);

  useEffect(() => {
    handleRefresh();
  }, [formId]);

  const handleRefresh = async () => {
    setLoadingFormAssociations(true);
    setSelectedAssociatedResourceArn(undefined);
    await getMetadataFormAssociations();
  };

  const getMetadataFormAssociations = async () => {
    setLoadingFormAssociations(true);
    setMetadataFormAssociations([]);
    setAllDataLoaded(false);
    try {
      // get all metadata associatedResources for an owner and filter on the results
      let result = await listResourcesAssociatedToMetadataEntity({
        Id: formId,
        OwnerId: formOwnerId,
        MaxResults: 4000,
      });
      let associatedResources = [];
      setNextToken(result.NextToken);
      if (result.NextToken == null) {
        setAllDataLoaded(true);
      }
      //rename to shorter name
      associatedResources.push(
        ...result.TableList,
        ...result.CatalogList,
        ...result.DatabaseList,
        ...result.ClusterList,
        ...result.ColumnList,
      );
      setMetadataFormAssociations(associatedResources);
    } catch (err) {
      setFormAssociationsTableMessage(`Unable to load form associations. ${err.message}`);
    } finally {
      setLoadingFormAssociations(false);
    }
  };

  const loadMoreFormAssociations = async () => {
    setLoadingFormAssociations(true);
    setMetadataFormAssociations([]);
    try {
      let result = await listResourcesAssociatedToMetadataEntity({
        Id: formId,
        OwnerId: formOwnerId,
        NextToken: nextToken,
        MaxResults: 4000,
      });
      let associatedResources = [];
      associatedResources.push(
        ...result.TableList,
        ...result.CatalogList,
        ...result.DatabaseList,
        ...result.ClusterList,
        ...result.ColumnList,
      );
      let currentMetadataFormAssociations = metadataFormAssociations;
      currentMetadataFormAssociations.push(...associatedResources);
      setMetadataFormAssociations(currentMetadataFormAssociations);
      setNextToken(result.NextToken);
      if (result.NextToken == null) {
        setAllDataLoaded(true);
      }
    } catch (err) {
      setFormAssociationsTableMessage(`Unable to load form associations. ${err.message}`);
    } finally {
      setLoadingFormAssociations(false);
    }
  };

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex >= paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreFormAssociations();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  const handleDissociateFormFromResource = async () => {
    setButtonLoading(true);
    try {
      let dissociateFormResult = await dissociateMetadataFormFromResource({
        ResourceArn: selectedAssociatedResourceArn,
        MetadataFormId: formId,
      });
      if (dissociateFormResult.Message == 'Success') {
        props.setNotification('success', 'Success');
      } else {
        props.setNotification('error', `Failure in dissociating form. Reason:${dissociateFormResult.Message}`);
      }
    } catch (err) {
      props.setNotification('error', `Error in dissociating form:${err.message}`);
    } finally {
      setButtonLoading(false);
      setDissociateFormModalVisible(false);
      await handleRefresh();
    }
  };

  const metadataFormAssociationTableButtonOptions = () => {
    return (
      <SpaceBetween size={'s'} direction={'horizontal'}>
        <Button variant={'icon'} iconName={'refresh'} onClick={handleRefresh} />
        <Button
          variant={'primary'}
          disabled={!selectedAssociatedResourceArn}
          onClick={() => setDissociateFormModalVisible(true)}
        >
          Dissociate
        </Button>
      </SpaceBetween>
    );
  };

  const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  return (
    <>
      <Modal
        visible={dissociateFormModalVisible}
        header={[`Confirm deletion`]}
        onDismiss={() => setDissociateFormModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='primary' loading={buttonLoading} onClick={handleDissociateFormFromResource}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          Are you sure you want to dissociate from resource <strong>{selectedAssociatedResourceArn}</strong>?
          <br />
        </TextContent>
      </Modal>
      <SpaceBetween size='l'>
        <Table
          {...collectionProps}
          selectionType='single'
          loadingText='Loading form associations...'
          loading={loadingFormAssociations}
          columnDefinitions={metadataFormAssociationColumnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          empty={
            <Box textAlign='center' color='inherit'>
              <b>No associations</b>
              <Box variant='p' color='inherit'>
                No associations to display.
              </Box>
            </Box>
          }
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Filter resources'
              filteringPlaceholder='Find resources'
              disabled={loadingFormAssociations}
              countText={getFilterCounterText(filteredItemsCount)}
            />
          }
          resizableColumns={true}
          header={
            <Header
              counter={`(${metadataFormAssociations ? metadataFormAssociations.length : '0'})`}
              actions={metadataFormAssociationTableButtonOptions()}
            >
              Resource associations
            </Header>
          }
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={notificationsPageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={paginationLabels}
              onChange={(e) => handlePageChange(e)}
              openEnd={!allDataLoaded}
              disabled={loadingFormAssociations}
            />
          }
        />
      </SpaceBetween>
    </>
  );
};

export default MetadataFormAssociationsTable;
